/**
 * Export Dialog
 *
 * Dialog for exporting agents, teams, or workflows to JSON
 */
import { useState } from 'react'
import { XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import Button from './Button'
import Card from './Card'

interface ExportDialogProps {
  isOpen: boolean
  onClose: () => void
  title: string
  itemCount: number
  itemType: string
  onExport: (options: ExportOptions) => void
}

export interface ExportOptions {
  format: 'json'
  pretty: boolean
  includeMetadata: boolean
}

export default function ExportDialog({
  isOpen,
  onClose,
  title,
  itemCount,
  itemType,
  onExport,
}: ExportDialogProps) {
  const [options, setOptions] = useState<ExportOptions>({
    format: 'json',
    pretty: true,
    includeMetadata: true,
  })

  if (!isOpen) return null

  const handleExport = () => {
    onExport(options)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg sm:text-xl font-bold text-foreground">{title}</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-muted-foreground p-2"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-4 mb-6">
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
            <p className="text-sm text-blue-600">
              Export <span className="font-semibold">{itemCount}</span> {itemType}
              {itemCount !== 1 ? 's' : ''} to JSON format
            </p>
          </div>

          {/* Format Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Export Format
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={options.format === 'json'}
                  onChange={() => setOptions({ ...options, format: 'json' })}
                  className="w-4 h-4 text-primary-600 border-border"
                />
                <span className="ml-2 text-sm text-foreground">
                  JSON (.json)
                </span>
              </label>
            </div>
          </div>

          {/* Options */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Export Options
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={options.pretty}
                  onChange={(e) =>
                    setOptions({ ...options, pretty: e.target.checked })
                  }
                  className="w-4 h-4 text-primary-600 border-border rounded"
                />
                <span className="ml-2 text-sm text-foreground">
                  Pretty print (readable formatting)
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={options.includeMetadata}
                  onChange={(e) =>
                    setOptions({ ...options, includeMetadata: e.target.checked })
                  }
                  className="w-4 h-4 text-primary-600 border-border rounded"
                />
                <span className="ml-2 text-sm text-foreground">
                  Include metadata (timestamp, version info)
                </span>
              </label>
            </div>
          </div>

          {/* Info */}
          <div className="bg-muted/50 border border-border rounded-lg p-3">
            <h4 className="text-xs font-semibold text-foreground mb-1">
              What gets exported?
            </h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Configuration and settings</li>
              <li>• Names, descriptions, and prompts</li>
              <li>• Tool and model configurations</li>
              <li className="text-muted-foreground">
                • IDs, timestamps, and workspace info are excluded
              </li>
            </ul>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={handleExport}
            variant="primary"
            className="flex-1"
          >
            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button onClick={onClose} variant="secondary" className="sm:w-auto">
            Cancel
          </Button>
        </div>
      </Card>
    </div>
  )
}
